import {
  Alert,
  AlertIcon,
  Box,
  Flex,
  Heading,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  useColorModeValue,
  Divider,
  IconButton,
  List,
  ListIcon,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  Tooltip,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import {
  FiBookOpen,
  FiCheckCircle,
  FiHelpCircle,
  FiTarget,
} from 'react-icons/fi';

import { AdminLayout, Breadcrumb } from '@grcs/components';

import { EvaluateEnhanced } from './Evaluate.enhanced';
import { GlossaryEnhanced } from './Glossary.enhanced';

export const ToolsPageEnhanced: React.FC = () => {
  const [activeTab, setActiveTab] = useState(() => {
    const savedTab = localStorage.getItem('toolsActiveTab');
    return savedTab === '1' ? 1 : 0;
  });

  const muted = useColorModeValue('gray.600', 'gray.300');
  const surface = useColorModeValue('white', 'gray.800');
  const border = useColorModeValue('gray.200', 'gray.600');

  const {
    isOpen: isHelpOpen,
    onOpen: onHelpOpen,
    onClose: onHelpClose,
  } = useDisclosure();

  useEffect(() => {
    localStorage.setItem('toolsActiveTab', activeTab.toString());
  }, [activeTab]);

  return (
    <AdminLayout padding={3}>
      <VStack spacing={4} align="stretch">
        <Box>
          <Breadcrumb
            items={[
              { label: 'Admin', href: '/admin' },
              { label: 'Tools', isCurrent: true },
            ]}
          />
          <Flex
            justify="space-between"
            align="center"
            gap={3}
            wrap="wrap"
            mt={3}
          >
            <Box>
              <Heading size="md">Teaching tools</Heading>
              <Text fontSize="sm" color={muted} mt={1}>
                Explore character lists and evaluate reading difficulty.
              </Text>
            </Box>
            <Tooltip label="Help & documentation">
              <IconButton
                size="sm"
                aria-label="Tools help"
                icon={<FiHelpCircle />}
                variant="outline"
                onClick={onHelpOpen}
              />
            </Tooltip>
          </Flex>
        </Box>
        <Box
          bg={surface}
          borderWidth="1px"
          borderColor={border}
          borderRadius="lg"
          p={{ base: 3, md: 4 }}
          minW={0}
        >
          <Tabs
            index={activeTab}
            onChange={setActiveTab}
            colorScheme="blue"
            isLazy
            lazyBehavior="keepMounted"
          >
            <TabList overflowX="auto">
              <Tab fontSize="sm" fontWeight="600" gap={2} flexShrink={0}>
                <FiBookOpen aria-hidden="true" />
                Character glossary
              </Tab>
              <Tab fontSize="sm" fontWeight="600" gap={2} flexShrink={0}>
                <FiTarget aria-hidden="true" />
                Article evaluator
              </Tab>
            </TabList>
            <TabPanels>
              <TabPanel px={0} pt={4} pb={0}>
                <GlossaryEnhanced active={activeTab === 0} embedded />
              </TabPanel>
              <TabPanel px={0} pt={4} pb={0}>
                <EvaluateEnhanced active={activeTab === 1} embedded />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>

        {/* Help Modal */}
        <Modal isOpen={isHelpOpen} onClose={onHelpClose} size="lg" isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Tools Help & Documentation</ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              <VStack spacing={6} align="stretch">
                <Alert status="info" variant="subtle">
                  <AlertIcon />
                  <VStack align="start" spacing={1} fontSize="sm">
                    <Text fontWeight="semibold">
                      Welcome to GRCS Teaching Tools
                    </Text>
                    <Text>
                      This dashboard provides comprehensive tools for Chinese
                      language education, including vocabulary analysis and
                      character exploration.
                    </Text>
                  </VStack>
                </Alert>

                <VStack spacing={4} align="stretch">
                  <Text fontWeight="semibold">Available Tools:</Text>

                  <Box>
                    <Text fontWeight="medium" color="green.600" mb={2}>
                      Character Glossary
                    </Text>
                    <Text fontSize="sm" color={muted} mb={2}>
                      Explore Chinese characters organized by difficulty level
                      and vocabulary lists.
                    </Text>
                    <List fontSize="sm" spacing={1}>
                      <ListItem>
                        <ListIcon as={FiCheckCircle} color="green.500" />
                        Browse GRCS and HSK character lists
                      </ListItem>
                      <ListItem>
                        <ListIcon as={FiCheckCircle} color="green.500" />
                        Search and filter characters by grade level
                      </ListItem>
                      <ListItem>
                        <ListIcon as={FiCheckCircle} color="green.500" />
                        View detailed character information with pronunciation
                        and meaning
                      </ListItem>
                    </List>
                  </Box>

                  <Box>
                    <Text fontWeight="medium" color="blue.600" mb={2}>
                      Vocabulary Evaluator
                    </Text>
                    <Text fontSize="sm" color={muted} mb={2}>
                      Analyze Chinese text to determine vocabulary difficulty
                      and readability.
                    </Text>
                    <List fontSize="sm" spacing={1}>
                      <ListItem>
                        <ListIcon as={FiCheckCircle} color="green.500" />
                        Evaluate text difficulty against vocabulary standards
                      </ListItem>
                      <ListItem>
                        <ListIcon as={FiCheckCircle} color="green.500" />
                        Generate readability scores and teaching recommendations
                      </ListItem>
                      <ListItem>
                        <ListIcon as={FiCheckCircle} color="green.500" />
                        Export analysis results for lesson planning
                      </ListItem>
                    </List>
                  </Box>
                </VStack>

                <Divider />

                <VStack spacing={2} align="stretch">
                  <Text fontWeight="semibold" fontSize="sm">
                    Need More Help?
                  </Text>
                  <Text fontSize="sm" color={muted}>
                    Contact support or check our documentation for detailed
                    usage instructions.
                  </Text>
                </VStack>
              </VStack>
            </ModalBody>
          </ModalContent>
        </Modal>
      </VStack>
    </AdminLayout>
  );
};
